import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './baseQuery';
import {
  RaffleCampaign,
  RaffleEntry,
  RaffleEvent,
  RaffleWinner
} from '../../resources/types/raffleTypes';

export const raffleApi = createApi({
  reducerPath: 'raffleApi',
  baseQuery,
  tagTypes: ['RaffleCampaign', 'RaffleEvent', 'RaffleEntry', 'RaffleWinner'],
  endpoints: builder => ({
    getRaffleCampaigns: builder.query<RaffleCampaign[], string | void>({
      query: () => 'reward/raffle/campaigns/admin',
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ['RaffleCampaign']
    }),
    createRaffleCampaign: builder.mutation<RaffleCampaign, Partial<RaffleCampaign>>({
      query: body => ({
        url: 'reward/raffle/campaigns',
        method: 'POST',
        body
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleCampaign']
    }),
    updateRaffleCampaign: builder.mutation<
      RaffleCampaign,
      { _id: string; body: Partial<RaffleCampaign> }
    >({
      query: ({ _id, body }) => ({
        url: `reward/raffle/campaigns/${_id}`,
        method: 'PUT',
        body
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleCampaign']
    }),
    deleteRaffleCampaign: builder.mutation<{ success: boolean }, string>({
      query: id => ({
        url: `reward/raffle/campaigns/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['RaffleCampaign', 'RaffleEvent']
    }),

    getRaffleEvents: builder.query<RaffleEvent[], string>({
      query: campaignId => ({
        url: 'reward/raffle/events/admin',
        params: { campaignId }
      }),
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ['RaffleEvent']
    }),
    createRaffleEvent: builder.mutation<RaffleEvent, Partial<RaffleEvent>>({
      query: body => ({
        url: 'reward/raffle/events',
        method: 'POST',
        body
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleEvent']
    }),
    updateRaffleEvent: builder.mutation<
      RaffleEvent,
      { _id: string; body: Partial<RaffleEvent> }
    >({
      query: ({ _id, body }) => ({
        url: `reward/raffle/events/${_id}`,
        method: 'PUT',
        body
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleEvent']
    }),
    deleteRaffleEvent: builder.mutation<{ success: boolean }, string>({
      query: id => ({
        url: `reward/raffle/events/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['RaffleEvent']
    }),
    drawRaffleEvent: builder.mutation<{ winners: string[] }, string>({
      query: id => ({
        url: `reward/raffle/events/${id}/draw`,
        method: 'POST'
      }),
      transformResponse: (res: any) => res?.data ?? { winners: [] },
      invalidatesTags: ['RaffleEvent', 'RaffleWinner']
    }),
    refundRaffleEvent: builder.mutation<
      { success: boolean; refundedEntries: number },
      string
    >({
      query: id => ({
        url: `reward/raffle/events/${id}/refund`,
        method: 'POST'
      }),
      invalidatesTags: ['RaffleEvent', 'RaffleEntry', 'RaffleWinner']
    }),

    getRaffleEntries: builder.query<RaffleEntry[], string>({
      query: eventId => `reward/raffle/events/${eventId}/entries`,
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ['RaffleEntry']
    }),
    getRaffleWinners: builder.query<RaffleWinner[], string>({
      query: eventId => `reward/raffle/events/${eventId}/winners`,
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ['RaffleWinner']
    }),
    markWinnerDelivered: builder.mutation<
      RaffleWinner,
      { eventId: string; userId: string }
    >({
      query: ({ eventId, userId }) => ({
        url: `reward/raffle/events/${eventId}/winners/${userId}/delivered`,
        method: 'PUT'
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleWinner']
    }),
    setWinnerTicket: builder.mutation<
      RaffleWinner,
      {
        eventId: string;
        userId: string;
        ticketQrCode: string;
        ticketPdfPath?: string | null;
      }
    >({
      query: ({ eventId, userId, ticketQrCode, ticketPdfPath }) => ({
        url: `reward/raffle/events/${eventId}/winners/${userId}/ticket`,
        method: 'PUT',
        body: { ticketQrCode, ticketPdfPath: ticketPdfPath ?? null }
      }),
      transformResponse: (res: any) => res?.data,
      invalidatesTags: ['RaffleWinner']
    })
  })
});

export const {
  useGetRaffleCampaignsQuery,
  useCreateRaffleCampaignMutation,
  useUpdateRaffleCampaignMutation,
  useDeleteRaffleCampaignMutation,
  useGetRaffleEventsQuery,
  useCreateRaffleEventMutation,
  useUpdateRaffleEventMutation,
  useDeleteRaffleEventMutation,
  useDrawRaffleEventMutation,
  useRefundRaffleEventMutation,
  useGetRaffleEntriesQuery,
  useGetRaffleWinnersQuery,
  useMarkWinnerDeliveredMutation,
  useSetWinnerTicketMutation
} = raffleApi;
